import { css } from "@linaria/core";
import CameraOveriewSpecs from "components/cameras/overview/CameraOveriewSpecs";
import CameraOverviewHero from "components/cameras/overview/CameraOverviewHero";
import AccordionSection from "components/common/AccordionSection";
import DeviceCards from "components/common/DeviceCards";
import FAQSection from "components/common/FAQSection";
import CTABannerRelay from "components/common/footer-banners/CTABannerRelay";
import IconCloud from "components/common/icon-components/IconCloud";
import IconNetwork from "components/common/icon-components/IconNetwork";
import IconPlugAndPlay from "components/common/icon-components/IconPlugAndPlay";
import IconSecureShield from "components/common/icon-components/IconSecureShield";
import { CTA_BUTTON_COPY_3 } from "components/data/landing-page-data";
import GlobalLayout from "components/page/GlobalLayout";
import { graphql, useStaticQuery } from "gatsby";
import React from "react";
import { Helmet } from "react-helmet";

const imageProps = {
  imgStyles: {
    borderRadius: "12px",
    width: "669px",
    maxWidth: "calc(100% - 2.5rem)",
    maxHeight: "416px",
  },
};

const RelayConnectorPage = () => {
  const imageData = useStaticQuery(graphql`
    query {
      heroImage: file(
        relativePath: {
          eq: "components/cameras/relay-connector/assets/relay-hub-hero.png"
        }
      ) {
        childImageSharp {
          gatsbyImageData(layout: FULL_WIDTH)
        }
      }
      n100: file(
        relativePath: {
          eq: "components/cameras/relay-connector/assets/n100-disco-card.png"
        }
      ) {
        childImageSharp {
          gatsbyImageData(layout: FULL_WIDTH)
        }
      }
      lite: file(
        relativePath: {
          eq: "components/cameras/relay-connector/assets/lite-disco-card.png"
        }
      ) {
        childImageSharp {
          gatsbyImageData(layout: FULL_WIDTH)
        }
      }
      acc1: file(
        relativePath: {
          eq: "components/cameras/relay-connector/assets/n500-lifestyle-min.png"
        }
      ) {
        childImageSharp {
          gatsbyImageData(layout: FULL_WIDTH)
        }
      }
      acc2: file(
        relativePath: {
          eq: "components/cameras/relay-connector/assets/trial-min.png"
        }
      ) {
        childImageSharp {
          gatsbyImageData(layout: FULL_WIDTH)
        }
      }
      acc3: file(
        relativePath: {
          eq: "components/cameras/relay-connector/assets/console-min.png"
        }
      ) {
        childImageSharp {
          gatsbyImageData(layout: FULL_WIDTH)
        }
      }
      acc4: file(
        relativePath: {
          eq: "components/cameras/relay-connector/assets/consolidate-min.png"
        }
      ) {
        childImageSharp {
          gatsbyImageData(layout: FULL_WIDTH)
        }
      }
      diagram: file(
        relativePath: {
          eq: "components/cameras/relay-connector/assets/relay-diagram.png"
        }
      ) {
        childImageSharp {
          gatsbyImageData(layout: FULL_WIDTH)
        }
      }
      dome: file(
        relativePath: { eq: "components/cameras/img/dome-explore-min.png" }
      ) {
        childImageSharp {
          gatsbyImageData(layout: FULL_WIDTH)
        }
      }
      fisheye: file(
        relativePath: { eq: "components/cameras/img/fisheye-explore-min.png" }
      ) {
        childImageSharp {
          gatsbyImageData(layout: FULL_WIDTH)
        }
      }
      bullet: file(
        relativePath: { eq: "components/cameras/img/bullet-explore-min.png" }
      ) {
        childImageSharp {
          gatsbyImageData(layout: FULL_WIDTH)
        }
      }
      multisensor: file(
        relativePath: { eq: "components/cameras/img/r600-card-min.png" }
      ) {
        childImageSharp {
          gatsbyImageData(layout: FULL_WIDTH)
        }
      }
      faq: file(
        relativePath: {
          eq: "components/cameras/relay-connector/assets/n100-lifestyle-min.png"
        }
      ) {
        childImageSharp {
          gatsbyImageData(layout: FULL_WIDTH)
        }
      }
    }
  `);

  return (
    <GlobalLayout dark color="var(--primary-bg)">
      <Helmet>
        <title>
          Full Cloud Benefits for Existing Cameras | Rhombus Relay | Cloud NVR
        </title>
        <meta
          name="description"
          content="Rhombus Relay provides flexible options to migrate existing cameras to cloud-managed physical security. Get a live demo to see how Rhombus' Cloud NVR solution can transform your existing cameras"
        />
        <meta
          name="thumbnail"
          content="https://rhombus.com/img/meta-relay-hub-min.png"
        />
        <meta
          property="og:image"
          content="https://rhombus.com/img/meta-relay-hub-min.png"
        />
      </Helmet>
      <CameraOverviewHero
        image={imageData.heroImage}
        imageAlt="Rhombus multisensor security camera displayed on a podium."
        imageClass={css`
          width: 626px;
          height: 570px;
          @media (max-width: 600px) {
            height: 370px;
          }
        `}
        preTitle="Relay connector"
        title="Transition to the Cloud, Achieve Full Interoperability"
        description="Relay is the simple, seamless way to migrate existing cameras to the Rhombus cloud-managed platform. With flexible deployment options, organizations can transition to the cloud, achieve a fully interoperable system, and reduce financial and installation burdens."
        primaryButton={{
          title: CTA_BUTTON_COPY_3,
          path: "/free-trial/",
        }}
        secondaryButton={null}
        backLink={false}
      />
      <DeviceCards
        data={{
          title: "Your Flexible Path to the Rhombus Platform",
          cards: [
            {
              name: "N100 Relay Core",
              description:
                "Transform Yesterday's Cameras into Tomorrow's Intelligence.",
              image: imageData.n100,
              altText:
                "network video recorders with cloud storage giving video surveillance",
              link: "n100",
            },
            {
              name: "Relay Lite",
              description:
                "No-Hardware Bridge to Existing Cameras for Extended Visibility.",
              image: imageData.lite,
              altText: "no hardware solution to replace nvr camera system",
              link: "lite",
            },
          ],
        }}
        compact
        center
      />
      <AccordionSection
        data={{
          title: "Your Bridge to Cloud-Managed Physical Security",
          items: [
            {
              ...imageProps,
              img: imageData.acc1,
              title: "Transition to the cloud on your terms",
              p: "Get instant cloud benefits without ripping and replacing your cameras. Integrate existing cameras with Relay Core's powerful on-premise device or Relay Lite's cost-effective no-hardware option for a unified security ecosystem.",
            },
            {
              ...imageProps,
              img: imageData.acc2,
              title: "Consolidate your organization’s tech stack",
              p: "Bring together all existing hardware and software for streamlined physical security. Consolidate cameras, access control, IoT sensors, and workflows to meet organizational needs today and in the future.",
            },
            {
              ...imageProps,
              img: imageData.acc3,
              title: "Save time in physical security",
              p: "Integrate existing cameras into the Rhombus platform in seconds. Simplify security operation with secure and remote access, AI detections, and real-time alerts.",
            },
            {
              ...imageProps,
              img: imageData.acc4,
              title: "Minimize financial impact",
              p: "Simplify the transition to the cloud. Migrate to cloud-managed physical security with a mix of existing cameras and Rhombus cameras to reduce costs associated with rip and replace.",
            },
          ],
        }}
      />
      <CameraOveriewSpecs
        title="Relay Specs"
        subTitle="Integrate Existing Cameras to the Rhombus Platform"
        images={[imageData.diagram]}
        specs={[
          {
            icon: <IconCloud />,
            text: "Flexible path to cloud-managed physical security",
          },
          {
            icon: <IconPlugAndPlay />,
            text: "Quick set-up with plug-and-play device or no hardware option",
          },
          {
            icon: <IconNetwork size="25" />,
            text: "Unified security ecosystem with full cloud benefits",
          },
          {
            icon: <IconSecureShield height="25" width="25" />,
            text: "Enterprise-level security, analytics, and interoperability",
          },
        ]}
      />
      <DeviceCards
        data={{
          title: "Your Flexible Path to the Rhombus Platform",
          cards: [
            {
              name: "Dome Cameras",
              description:
                "Modern enterprise performance with edge-based analytics.",
              image: imageData.dome,
              altText: "",
              link: "/cameras/dome-cameras/",
            },
            {
              name: "Fisheye Cameras",
              description:
                "Immersive coverage and visibility with minimal camera placement.",
              image: imageData.fisheye,
              altText: "",
              link: "/cameras/fisheye-cameras/",
            },
            {
              name: "Bullet Cameras",
              description: "Ruggedized form-factor with high-resolution video.",
              image: imageData.bullet,
              altText: "",
              link: "/cameras/bullet-cameras/",
            },
            {
              name: "Multisensor Camera",
              description: "One powerful camera for complete coverage.",
              image: imageData.multisensor,
              altText: "",
              link: "/cameras/multisensor-cameras/",
            },
          ],
        }}
        recommend
        deviceType={"Camera"}
      />
      <FAQSection
        image={imageData.faq}
        reverse
        title="Relay FAQs"
        QA={[
          {
            question: "What cameras can be connected to Relay?",
            answer:
              "Relay Core N100 is able to connect any camera with RTSP streaming. The N100 uses ONVIF protocols, when supported, to ingest third-party cameras. Once camera streams reach the Rhombus device, video footage and cloud communication are fully encrypted. Users will need to manage updates to their non-Rhombus cameras to ensure they have the most updated firmware.",
          },
          {
            question:
              "What benefits does a cloud-based camera system provide versus an on-premise NVR?",
            answer:
              "A cloud-based camera system provides easier scalability and streamlines security operations. With a cloud infrastructure, you get access to secure, remote access desktop console and mobile app to view and manage all your surveillance cameras. In addition, you can also get AI analytics, real-time alerts, and automated workflows. Unlike an NVR system or DVR system, a cloud-based camera system with cloud storage allows video footage to be stored remotely over the internet, eliminating the need for onsite recording devices and offering enhanced accessibility.",
          },
          {
            question: "Does Relay Core make my current NVR obsolete?",
            answer:
              "With Relay Core, you would not need the additional hardware of your current NVR. However, for additional on-premise storage, you can keep your current NVR. Traditional NVR camera systems have limitations such as scalability challenges, the need for complex infrastructure, and less flexible access options compared to cloud-based solutions. With the N100, users can view footage, access an advanced video management system, from a secure single dashboard.",
          },
          {
            question: "Is Relay Core NDAA compliant?",
            answer:
              "A Cloud NVR (Network Video Recorder) provides many benefits to improve an organization's video surveillance capabilities. This includes reduced hardware costs, increased scalability, and enhanced reliability. With a Cloud NVR solution, users can access their video footage from anywhere, at any time, using a web browser or mobile app. This allows for remote monitoring for instant access and management of security cameras, making it an ideal solution for businesses with multiple locations. Cloud NVR systems provide automatic software updates and maintenance for the NVR box, reducing the need for manual intervention and minimizing downtime. This ensures that your video surveillance system is always up-to-date and functioning optimally, providing continuous protection for your assets.",
          },
        ]}
      />
      <CTABannerRelay />
    </GlobalLayout>
  );
};

export default RelayConnectorPage;
